// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Checkbox, TextField, Box, Chip } from '@mui/material';
import { WizardUtils } from '../../../utils/WizardUtils';

export const AutocompleteField = ({ value, onChange, options, disabled = false, col = {}, currentRow = {}, id }) => {
  const autocompleteProps = col.autocompleteProps || {};

  const [inputValue, setInputValue] = useState('');

  const getCleanOptionLabels = useCallback(() => {
    const cleanOptions = col?.options || options || [];
    return cleanOptions.map((option) => option?.label ?? option);
  }, [col?.options, options]);

  const processedOptions = useMemo(() => {
    if (typeof col.getOptions === 'function') {
      const result = col.getOptions(currentRow);
      const dynamicOptions = Array.isArray(result) ? result.map((option) => option?.label ?? option) : [];
      return dynamicOptions.filter((a) => a != null).sort((a, b) => String(a).localeCompare(String(b)));
    }

    const baseOptions = [...(col?.options?.map((option) => option?.label ?? option) ?? options ?? [])];
    if (value) {
      const selectedTasks = value.split('|').filter(Boolean);
      selectedTasks.forEach((selectedTask) => {
        const trimmedTask = selectedTask.trim();
        const cleanOptions = col?.options || options || [];

        if (!cleanOptions.includes(trimmedTask) && !baseOptions.includes(trimmedTask)) {
          baseOptions.push(trimmedTask);
        }
      });
    }

    return baseOptions.filter((a) => a != null).sort((a, b) => String(a).localeCompare(String(b)));
  }, [col, currentRow, options, value]);

  const processedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.length > 0) {
      return value.split('|');
    }
    return [];
  }, [value]);

  const SELECT_ALL_OPTION = '__SELECT_ALL__';

  const handleAutocompleteChange = (e, newValue) => {
    if (newValue.includes(SELECT_ALL_OPTION)) {
      // Determine the currently filtered options
      const filteredOptions = processedOptions.filter((option) =>
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      const allFilteredSelected = filteredOptions.every((option) => processedValue.includes(option));

      let updatedValue;
      if (allFilteredSelected) {
        // Deselect all filtered options
        updatedValue = processedValue.filter((v) => !filteredOptions.includes(v));
      } else {
        // Select all filtered options (keep existing selections too)
        updatedValue = [...new Set([...processedValue, ...filteredOptions])];
      }
      onChange(updatedValue.join('|'));
      return;
    }
    const joinedValue = Array.isArray(newValue) ? newValue.join('|') : newValue;
    onChange(joinedValue);
  };

  const handleInputChange = (e, newInputValue, reason) => {
    if (autocompleteProps.isSearchable) {
      if (reason === 'input') {
        setInputValue(newInputValue);
      }
      // Don't clear the input value when selecting options - let user continue searching
      return;
    }

    setInputValue(newInputValue);
  };

  const handleBlur = () => {
    if (autocompleteProps.isSearchable) {
      setInputValue('');
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect={autocompleteProps.disableCloseOnSelect ?? true}
      limitTags={autocompleteProps.checkbox ? 1 : 20}
      id={autocompleteProps.id ?? id ?? 'autocomplete-field'}
      options={processedOptions}
      freeSolo={autocompleteProps.freeSolo ?? true}
      fullWidth
      disableClearable={autocompleteProps.disableClearable ?? true}
      sx={{
        ...(autocompleteProps.isSearchable && {
          maxWidth: WizardUtils.getMaxCharWidth(col.length),
          '& .MuiInputBase-root.MuiOutlinedInput-root': { paddingRight: '32px', minHeight: '56px' },
        }),
      }}
      disabled={disabled}
      forcePopupIcon={autocompleteProps.isSearchable ?? true}
      slotProps={{
        popper: {
          sx: {
            width: '350px!important',
          },
        },
      }}
      value={processedValue}
      onChange={handleAutocompleteChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderTags={(value, getTagProps) => {
        if (autocompleteProps.isSearchable) {
          const joinedValue = value.join('|');
          return (
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                width: '100%',
              }}
            >
              {joinedValue}
            </Box>
          );
        }

        return value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
        });
      }}
      filterOptions={(options, params) => {
        const filtered = options.filter(
          (option) =>
            option !== SELECT_ALL_OPTION && option.toLowerCase().includes((params.inputValue || '').toLowerCase())
        );
        if (autocompleteProps.checkbox && filtered.length > 0) {
          return [SELECT_ALL_OPTION, ...filtered];
        }
        return filtered;
      }}
      renderOption={(props, option, { selected }) => {
        if (option === SELECT_ALL_OPTION) {
          // eslint-disable-next-line react/prop-types
          const { key, ...optionProps } = props;
          const filteredOptions = processedOptions.filter((opt) =>
            opt.toLowerCase().includes(inputValue.toLowerCase())
          );
          const allFilteredSelected =
            filteredOptions.length > 0 && filteredOptions.every((opt) => processedValue.includes(opt));

          return (
            <li
              key="select-all"
              {...optionProps}
              data-cy="autocomplete-option-select-all"
              style={{ ...optionProps.style, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}
            >
              <Checkbox style={{ marginRight: 8 }} checked={allFilteredSelected} />
              Tout sélectionner
            </li>
          );
        }
        if (autocompleteProps.checkbox) {
          // eslint-disable-next-line react/prop-types
          const { key, ...optionProps } = props;
          const cleanOptionLabels = getCleanOptionLabels();
          const isRemovedOption = !cleanOptionLabels.includes(option);

          return (
            <li
              key={key}
              {...optionProps}
              data-cy={`autocomplete-option-${option}`}
              style={{
                ...optionProps.style,
              }}
            >
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {isRemovedOption ? `${option} (supprimée)` : option.title || option}
            </li>
          );
        }
        return (
          <li {...props} data-cy={`autocomplete-option-${option}`}>
            {option}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{
            ...(autocompleteProps.isSearchable && {
              '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: 0, fontSize: '14px' },
            }),
          }}
          {...(autocompleteProps.isSearchable && {
            onKeyDown: (event) => {
              if (event.key === 'Backspace' || event.key === 'Delete') {
                event.stopPropagation();
              }
            },
          })}
          onBlur={handleBlur}
          placeholder={autocompleteProps.placeholder || ''}
        />
      )}
    />
  );
};

AutocompleteField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  col: PropTypes.object,
  currentRow: PropTypes.object,
  id: PropTypes.string,
};
